export interface IYouTubePlayer {
  playVideo(): void
  pauseVideo(): void
  loadVideoById(videoId: string): void
  getDuration(): number
  getCurrentTime(): number
  destroy(): void
}

export interface IYouTubePlayerStatic {
  new (
    container: HTMLElement | string,
    options: {
      videoId: string
      playerVars?: Record<string, number | string>
      events?: {
        onReady?: () => void
        onStateChange?: (event: { data: number }) => void
        onError?: (event: { data: number }) => void
      }
    },
  ): IYouTubePlayer
}

export interface IYouTubeAPI {
  Player: IYouTubePlayerStatic
  PlayerState: {
    ENDED: number
    PLAYING: number
    PAUSED: number
    BUFFERING: number
  }
}

declare global {
  interface Window {
    YT?: IYouTubeAPI
    onYouTubeIframeAPIReady?: () => void
  }
}

let youtubeAPIPromise: Promise<IYouTubeAPI> | null = null

export function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /[?&]v=([a-zA-Z0-9_-]{11})/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match?.[1]) return match[1]
  }

  return null
}

export function normalizeYouTubeUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url)
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : null
}

export function loadYouTubeAPI(): Promise<IYouTubeAPI> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API is only available in browser"))
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT)
  }

  if (youtubeAPIPromise) {
    return youtubeAPIPromise
  }

  youtubeAPIPromise = new Promise<IYouTubeAPI>((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady

    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.()

      if (window.YT?.Player) {
        resolve(window.YT)
      } else {
        youtubeAPIPromise = null
        reject(new Error("YouTube API loaded, but YT.Player is unavailable"))
      }
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://www.youtube.com/iframe_api"]')

    if (!existingScript) {
      const tag = document.createElement("script")
      tag.src = "https://www.youtube.com/iframe_api"
      tag.async = true
      tag.onerror = () => {
        youtubeAPIPromise = null
        reject(new Error("Failed to load YouTube iframe API"))
      }

      document.body.appendChild(tag)
    }
  })

  return youtubeAPIPromise
}
